/*
 *******************************************************************************
 *
 *  Filename:   /redux/slices/AIMedicalNotes.js
 *
 *  Syntax:     NA
 *
 *  Synopsis:   AI Medical Notes redux reducer
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Thursday, June 22, 2023 - 12:05 AM (CST)
 *
 *  Notes:
 *
 *  Revisions:
 *              2023/06/22  NJN Initial Build
 *
 *  Copyright (c) 2023 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  transcription: "",
  transcriptionArray: [],
  wordTokens: [],
  wordTokenArray: [],
  entities: [],
  activeBox: null,
  summary: "",
  error: null,
};

const updateFieldReducer = (state, action, field) => {
  if (action.payload !== null) {
    state[field] = action.payload[field];
    state.error = null;
  } else {
    state.error = `No ${field} provided.`;
  }
};

const updateFieldArray = (state, action, field) => {
  if (action.payload !== null) {
    state[field] = [...state[field], action.payload];
    state.error = null;
  } else {
    state.error = `No ${field} provided.`;
  }
};

const clearFieldArray = (state, action, field) => {
  state[field] = [];
  state.error = null;
};

export const aiMedicalNotesSlice = createSlice({
  name: "aiMedicalNotes",
  initialState,
  reducers: {
    updateActiveBox: (state, action) => updateFieldReducer(state, action, "activeBox"),
    updateTranscription: (state, action) => updateFieldReducer(state, action, "transcription"),
    updateTranscriptionArray: (state, action) => updateFieldArray(state, action, "transcriptionArray"),
    updateWordTokens: (state, action) => updateFieldArray(state, action, "wordTokens"),
    updateWordTokenArray: (state, action) => updateFieldArray(state, action, "wordTokenArray"),
    updateEntities: (state, action) => updateFieldArray(state, action, "entities"),
    clearTranscriptionArray: (state, action) => clearFieldArray(state, action, "transcriptionArray"),
    updateSummary: (state, action) => updateFieldReducer(state, action, "summary"),
    clearWordTokens: (state, action) => clearFieldArray(state, action, "wordTokens"),
    clearWordTokenArray: (state, action) => clearFieldArray(state, action, "wordTokenArray"),
    clearEntities: (state, action) => clearFieldArray(state, action, "entities"),
    replaceTranscriptionArrayItem: (state, action) => {
      const { i, text } = action.payload;
      if (state.transcriptionArray[i]) {
        state.transcriptionArray = state.transcriptionArray.map((item, index) => (index === i ? { text } : item));
        state.error = null;
      } else {
        state.error = `No item at index ${i} to replace.`;
      }
    },
    replaceWordTokenArrayItem: (state, action) => {
      const { i, transcriptWordList } = action.payload;
      if (state.wordTokenArray[i]) {
        state.wordTokenArray = state.wordTokenArray.map((item, index) =>
          index === i ? { startTime: 1.0, wordTokens: transcriptWordList } : item,
        );
        state.error = null;
      } else {
        state.error = `No item at index ${i} to replace.`;
      }
    },
    replaceWordTokenItem: (state, action) => {
      const { i, transcriptWordList } = action.payload;
      if (state.wordTokens[i]) {
        state.wordTokens = state.wordTokens.map((item, index) => (index === i ? transcriptWordList : item));
        state.error = null;
      } else {
        state.error = `No item at index ${i} to replace.`;
      }
    },
    replaceEntitiesItem: (state, action) => {
      const { i, entities } = action.payload;
      if (state.entities[i]) {
        state.entities = state.entities.map((item, index) => (index === i ? entities : item));
        state.error = null;
      } else {
        state.error = `No item at index ${i} to replace.`;
      }
    },
  },
});

export const {
  updateActiveBox,
  updateTranscription,
  updateTranscriptionArray,
  updateWordTokens,
  updateWordTokenArray,
  updateEntities,
  updateSummary,
  clearTranscriptionArray,
  clearWordTokens,
  clearWordTokenArray,
  clearEntities,
  replaceTranscriptionArrayItem,
  replaceWordTokenItem,
  replaceWordTokenArrayItem,
  replaceEntitiesItem,
} = aiMedicalNotesSlice.actions;

export default aiMedicalNotesSlice.reducer;
